import { useQuery } from "@apollo/client";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { FullScreenDocumentPreview } from "components/Document/shared";
import { extensionFromName } from "components/Document/shared/util/extensionFromName";
import {
  partitionReviewersAndSigners,
  partitionSubmissionDocuments,
  sortSubmissionDocumentsByType,
} from "components/Submission/helpers/documentHelpers";
import { PaymentIssueBadge } from "components/Submission/PaymentIssueBadge";
import { SignaturePreparationBanner } from "components/Submission/SignaturePreparationBanner";
import { SubmissionCommentStream } from "components/Submission/SubmissionCommentStream";
import { SubmissionDisplayReviewOrSignDocument } from "components/Submission/SubmissionDisplayReviewOrSignDocument";
import { SubmissionDisplayStatic } from "components/Submission/SubmissionDisplayStatic";
import { SubmissionHeaderNavigation } from "components/Submission/SubmissionHeaderNavigation";
import { SubmissionLinkedRecords } from "components/Submission/SubmissionLinkedRecords";
import { SubmissionRevisionBanner } from "components/Submission/SubmissionRevisionBanner";
import {
  GetSubmissionQuery,
  SignatureStatus,
  SignatureType,
  SubmissionDocumentType,
  SubmissionStatus,
} from "generated-graphql/graphql";
import { SubmissionReviewState } from "hooks/submission/reviewStates";
import { useSignatureConfirmation } from "hooks/submission/useSignatureConfirmation";
import { useSubmissionDocumentPreview } from "hooks/submission/useSubmissionDocumentPreview";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useDocumentDownloadLink } from "hooks/useDocumentDownloadLink";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useSubmissionForm } from "hooks/useSubmissionForm";
import {
  useApproveSubmissionDocumentReview,
  useGetSubmission,
  useRejectSubmissionDocumentReview,
} from "hooks/useSubmissionMutations";
import { GET_EMBEDDED_DOCUMENT_SIGNING_URL } from "queries/submissions";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { openInNewTabFromPromise } from "util/download";
import { stepperFlagForSubmission } from "util/wasteStepperFlags";
import { buildWasteFacilityUrl } from "util/wasteUrls";
import { STATUS_LABELS } from "utils/submissionStatusLabels";

import { getSavedWasteSubmissionsUrl } from "../navigation";
import { pickTransitionErrorMessage } from "./pickTransitionErrorMessage";

type Submission = NonNullable<GetSubmissionQuery["submission"]>;

type ReviewFormValues = {
  reviewFinished: boolean;
  rejectionComment: string;
};

function Title({
  submission,
  reviewState,
  isStaff,
}: {
  submission: Submission;
  reviewState: SubmissionReviewState;
  isStaff: boolean;
}) {
  const titleTextAndToolTip = useMemo(() => {
    if (submission.status === SubmissionStatus.ProcessingSubmission) {
      return {
        title: "Processing Submission",
        tooltip:
          "The submission is being processed by Encamp Staff and will be updated when completed",
      };
    }

    if (
      // Staff never need to review or sign a document
      isStaff ||
      // Nothing to review/sign or review/sign is complete
      reviewState === SubmissionReviewState.NO_DOCUMENTS_ASSIGNED ||
      reviewState === SubmissionReviewState.DOCUMENT_REVIEW_COMPLETED
    ) {
      return {
        title: "View Submission",
        tooltip: null,
      };
    }

    // We display a disabled form when SubmissionReviewState.SIGNER_IS_WAITING_FOR_REVIEWERS
    // for now we are showing the same title to user for both states
    if (
      reviewState === SubmissionReviewState.SIGNER_CAN_SIGN ||
      reviewState === SubmissionReviewState.SIGNER_IS_WAITING_FOR_REVIEWERS
    ) {
      return {
        title: "Sign Submission",
        tooltip:
          "Review the submission, then add your signature and complete approval. If edits are needed, use the comment feature and mark the submission as Needs Edits. \n Once the signature step is completed, Encamp will file the submission with the appropriate regulatory body.",
      };
    } else {
      return {
        title: "Review Submission",
        tooltip:
          "Review the submission, then click Ready for Signature and Complete Review. If edits are needed, use the comment feature and mark the submission as Needs Edits and it will be sent back to Encamp for edits to be made.",
      };
    }
  }, [reviewState, submission, isStaff]);

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Typography variant="h5" component="h1">
        {titleTextAndToolTip.title}
      </Typography>
      {titleTextAndToolTip.tooltip && (
        <Tooltip title={titleTextAndToolTip.tooltip}>
          <InfoOutlinedIcon sx={{ fontSize: 20, color: "text.secondary" }} />
        </Tooltip>
      )}
    </Stack>
  );
}

export function ReadOnlySubmission() {
  const { tenantId, submissionId } = useParams();
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, isStaff } = useCurrentUser();
  const { featureFlags, loading: featureFlagsLoading } = useFeatureFlags();

  const submissionsUrl = getSavedWasteSubmissionsUrl(tenantId ?? "", user?.email);

  const { data, loading, error, refetch } = useGetSubmission(submissionId || "", !submissionId);
  const refetchSubmission = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const stepperUrl = `/o/${tenantId}/waste/submissions/${submissionId}/stepper`;

  useBreadcrumb([
    { label: "Waste", to: `/o/${tenantId}/waste` },
    { label: "Submissions", to: submissionsUrl },
    { label: "View Submission" },
  ]);

  // All hooks must be called before any conditional returns
  const alerts = useAlerts();
  const [previewDocumentId, setPreviewDocumentId] = useState<string | null>(null);
  const [previewDocumentName, setPreviewDocumentName] = useState<string | undefined>();
  const [fullScreenPreviewOpen, setFullScreenPreviewOpen] = useState(false);
  const hasAutoSelected = useRef(false);
  const [signingDialogOpen, setSigningDialogOpen] = useState(false);
  const handleDocumentPreviewError = useCallback(
    (error: unknown) => {
      console.error("Error fetching document download link:", error);
      setFullScreenPreviewOpen(false);
      alerts.error("Error loading document preview");
    },
    [alerts]
  );
  const { documentUrl: previewDocumentUrl } = useSubmissionDocumentPreview({
    documentId: previewDocumentId,
    onError: handleDocumentPreviewError,
  });

  // Separate lazy query for the "open in new tab" button; inline disposition so
  // the browser renders the document instead of downloading it
  const [getDocumentDownloadLinkForNewTab] = useDocumentDownloadLink();

  const {
    comments,
    setComments,
    currentUsersSubmissionDocumentReviewId,
    reviewState,
    isReviewerASigner,
  } = useSubmissionForm({
    submissionId: data?.submission?.id || "",
    user,
  });

  const { control, handleSubmit, watch, formState, setValue } = useForm<ReviewFormValues>({
    defaultValues: { reviewFinished: true, rejectionComment: "" },
  });
  const reviewFinished = watch("reviewFinished");
  const rejectionComment = watch("rejectionComment");

  const [approveSubmissionDocumentReview, { loading: approveLoading }] =
    useApproveSubmissionDocumentReview();
  const [rejectSubmissionDocumentReview, { loading: rejectLoading }] =
    useRejectSubmissionDocumentReview();

  const currentUserReviewer = data?.submission?.reviewers?.find((r) => r.user.id === user?.id);
  const selectedSubmissionDocument = data?.submission?.documents?.find(
    (document) =>
      document.document.id === previewDocumentId &&
      document.type === SubmissionDocumentType.Submission
  );
  const isPreviewedDocumentPreparedForSigning =
    selectedSubmissionDocument?.externalDocumentId != null &&
    (currentUserReviewer?.signatureStatus === SignatureStatus.Sent ||
      currentUserReviewer?.signatureStatus === SignatureStatus.Completed);

  const { data: signingUrlData } = useQuery(GET_EMBEDDED_DOCUMENT_SIGNING_URL, {
    variables: { submissionId: data?.submission?.id || "" },
    fetchPolicy: "network-only",
    // We only need to fetch the signing URL if the signer is able to sign
    skip:
      !data?.submission?.id ||
      data.submission.status !== SubmissionStatus.InReview ||
      reviewState !== SubmissionReviewState.SIGNER_CAN_SIGN ||
      !isPreviewedDocumentPreparedForSigning,
  });

  const submissionDocumentReviewId = currentUsersSubmissionDocumentReviewId;

  const isMutating = approveLoading || rejectLoading || formState.isSubmitting;

  const handleReviewSubmit = useCallback(
    async (values: ReviewFormValues) => {
      if (!submissionDocumentReviewId) {
        alerts.error("Unable to locate a document review to update.");
        return;
      }

      try {
        if (values.reviewFinished) {
          await approveSubmissionDocumentReview({
            variables: { submissionDocumentReviewId },
          });

          if (reviewState === SubmissionReviewState.SIGNER_CAN_SIGN) {
            alerts.success(
              "Thank you for signing! This status has updated to ✅ Awaiting Submission."
            );
          } else {
            alerts.success("Review completed\nThank you for completing your review!");
          }
        } else {
          const rejectionComment = values.rejectionComment.trim();
          await rejectSubmissionDocumentReview({
            variables: { submissionDocumentReviewId, rejectionComment },
          });
          alerts.success(
            "Review completed\nThank you for completing your review! This submission was sent back to Encamp for edits."
          );
        }
        navigate(submissionsUrl);
      } catch (error) {
        console.error("Failed to complete submission review", error);
        const safeMessage = pickTransitionErrorMessage(error);
        if (safeMessage) {
          alerts.warning(`Your review was saved. ${safeMessage}`);
        } else {
          alerts.error("Unable to complete review. Please try again.");
        }
      }
    },
    [
      alerts,
      navigate,
      approveSubmissionDocumentReview,
      rejectSubmissionDocumentReview,
      submissionDocumentReviewId,
      reviewState,
      submissionsUrl,
    ]
  );

  const handleDocumentPreview = useCallback((document: { id: string; name?: string }) => {
    if (document?.id) {
      setPreviewDocumentId(document.id);
      setPreviewDocumentName(document.name);
      setFullScreenPreviewOpen(true);
    }
  }, []);

  const handleDocumentOpenInNewTab = useCallback(
    (document: { id: string }) => {
      if (!document.id) {
        return;
      }

      // The tab is claimed synchronously inside the gesture — `window.open`
      // after the query resolves is popup-blocked in Safari.
      void openInNewTabFromPromise(async () => {
        const { data: linkData } = await getDocumentDownloadLinkForNewTab({
          variables: { id: document.id, inline: true },
        });
        if (!linkData?.getDocumentDownloadLink) {
          alerts.error("Unable to open document");
        }
        return linkData?.getDocumentDownloadLink;
      }).catch((error: unknown) => {
        console.error("Error fetching document download link:", error);
        alerts.error("Error opening document");
      });
    },
    [alerts, getDocumentDownloadLinkForNewTab]
  );

  const handleSigningDialogClose = useCallback(() => {
    setSigningDialogOpen(false);
  }, []);

  const handleSigningDialogOpen = useCallback(() => {
    setSigningDialogOpen(true);
  }, []);

  // Extract signer from reviewers
  // we should probably not assume 1 signer, currently we really only support 1 signer
  const { signer, reviewers } = useMemo(() => {
    if (!data?.submission) {
      return { signer: undefined, reviewers: [] };
    }
    return partitionReviewersAndSigners(data.submission.reviewers || []);
  }, [data?.submission]);

  const facilities = useMemo(() => {
    const facilitySubmissions = data?.submission?.facilitySubmissions ?? [];
    return facilitySubmissions
      .map((fs) => {
        const facility = fs.facility;
        if (!facility) return null;
        const epaAlt = facility.facilityAlternateIds?.find((a) => a.type === "EPA" && !a.hasNoId);
        return {
          display: `${facility.name}${
            facility.customerFacilityId ? ` (${facility.customerFacilityId})` : ""
          } - ${facility.streetAddress1}`,
          epaId: epaAlt?.value ?? null,
          href: tenantId ? buildWasteFacilityUrl(tenantId, facility.id) : null,
        };
      })
      .filter((f): f is NonNullable<typeof f> => f !== null);
  }, [data?.submission, tenantId]);

  // Separate submission documents from attachments and sort by type
  const { submissionDocuments: unsortedSubmissionDocuments, attachmentDocuments } = useMemo(() => {
    if (!data?.submission) {
      return { submissionDocuments: [], attachmentDocuments: [] };
    }
    const docs = (data.submission.documents || []).map((doc) => ({
      id: doc.document.id,
      name: doc.document.title || "Untitled",
      documentType: doc.document.documentType || "PDF",
      submissionDocumentType: doc.type,
      url: doc.document.storageLink,
    }));
    return partitionSubmissionDocuments(docs);
  }, [data?.submission]);

  const submissionDocuments = useMemo(
    () => sortSubmissionDocumentsByType(unsortedSubmissionDocuments),
    [unsortedSubmissionDocuments]
  );

  useEffect(() => {
    if (data?.submission) {
      setComments(data.submission.comments || []);
    }
  }, [data?.submission, setComments]);

  const {
    signingCompleted,
    signingFlowConfirmed,
    signingConfirmationPending,
    signatureConfirmationState,
    isAwaitingPostSignatureTransition,
    markSignatureSubmitted,
    retrySignatureConfirmation,
  } = useSignatureConfirmation({
    currentUserId: user?.id,
    reviewers: data?.submission?.reviewers ?? [],
    submissionStatus: data?.submission?.status,
    refetchSubmission,
  });

  useEffect(() => {
    if (!currentUserReviewer) {
      return;
    }

    if (currentUserReviewer.reviewerStatus === "APPROVED") {
      setValue("reviewFinished", true, { shouldDirty: false });
    } else if (currentUserReviewer.reviewerStatus === "REJECTED") {
      setValue("reviewFinished", false, { shouldDirty: false });
    }
  }, [currentUserReviewer, setValue]);

  useEffect(() => {
    if (reviewFinished && rejectionComment.trim().length > 0) {
      setValue("rejectionComment", "", { shouldDirty: false });
    }
  }, [reviewFinished, rejectionComment, setValue]);

  // Auto-select first document on mount
  useEffect(() => {
    // Only auto-select once on initial mount
    if (!hasAutoSelected.current) {
      if (submissionDocuments.length > 0) {
        const firstDoc = submissionDocuments[0];
        if (firstDoc?.id) {
          hasAutoSelected.current = true;
          setPreviewDocumentId(firstDoc.id);
          setPreviewDocumentName(firstDoc.name);
        }
      } else if (attachmentDocuments.length > 0) {
        const firstAttachment = attachmentDocuments[0];
        if (firstAttachment?.id) {
          hasAutoSelected.current = true;
          setPreviewDocumentId(firstAttachment.id);
          setPreviewDocumentName(firstAttachment.name);
        }
      }
    }
  }, [submissionDocuments, attachmentDocuments]);

  // Now we can have conditional returns after all hooks are called
  if (loading || featureFlagsLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ padding: 3 }}>
        <Alert severity="error">Failed to load submission: {error.message}</Alert>
      </Box>
    );
  }

  if (!data?.submission) {
    return (
      <Box sx={{ padding: 3 }}>
        <Alert severity="warning">Submission not found</Alert>
      </Box>
    );
  }

  const submission = data.submission;

  // Every submission has steps once backfilled, so this applies to everyone —
  // staff (who can edit) and customers (read-only), mirroring the Tier II
  // report flow. The governing flag depends on the submission's archetype
  // (interactive vs document form) — the same dispatch the stepper shell's own
  // redirect uses. Only redirect while that flag is on — the shell navigates
  // back here when it's off, so redirecting would loop; with the flag off this
  // detail view is the degraded read-only fallback. Document forms in
  // review/signing stay in the stepper too (Review & sign owns them).
  const stepperEnabled =
    featureFlags[stepperFlagForSubmission(submission.usesInteractiveStepper)] === true;
  if (stepperEnabled) {
    return <Navigate to={stepperUrl} replace />;
  }

  const handleEditSubmission = () => {
    navigate(`/o/${tenantId}/waste/submissions/${submissionId}/edit`);
  };

  const isDigitalSigner = isReviewerASigner && submission.signatureType === SignatureType.Digital;

  const showReviewOrSignPanel =
    !(isDigitalSigner && signingCompleted) &&
    (reviewState === SubmissionReviewState.SIGNER_CAN_SIGN ||
      reviewState === SubmissionReviewState.SIGNER_IS_WAITING_FOR_REVIEWERS ||
      (reviewState === SubmissionReviewState.REVIEWER_CAN_REVIEW && !isDigitalSigner));

  return (
    <Stack>
      {tenantId && <SubmissionHeaderNavigation tenantId={tenantId} />}

      {/* Main Content Area - matches index page background */}
      <Stack
        id="submission-view"
        p={2}
        sx={{
          background:
            theme.palette.mode === "light" ? theme.palette.grey50.light : theme.palette.grey50.dark,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            display: "flex",
            flexDirection: "column",
            position: "relative",
          }}
        >
          <Box
            sx={{
              p: 3,
              pb: 0,
            }}
          >
            {/* Header Section */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Title isStaff={isStaff} reviewState={reviewState} submission={submission} />
                <PaymentIssueBadge isStaff={isStaff} hasPaymentIssue={submission.hasPaymentIssue} />
              </Stack>

              {isStaff && (
                <Stack direction="row" spacing={1}>
                  <Button
                    component="a"
                    href={`/staff/fulfillment/waste/submissions/${submissionId}`}
                    target="_blank"
                    variant="outlined"
                    color="staff"
                    endIcon={<OpenInNewIcon />}
                  >
                    Open Fulfillment
                  </Button>
                  <Button
                    variant="contained"
                    color="staff"
                    startIcon={<EditIcon />}
                    onClick={handleEditSubmission}
                  >
                    Edit Submission
                  </Button>
                </Stack>
              )}
            </Stack>
          </Box>

          {submission.status === SubmissionStatus.NotReporting && (
            <Alert severity="info" sx={{ mx: 3, mb: 2 }}>
              This submission is marked as{" "}
              <strong>{STATUS_LABELS[SubmissionStatus.NotReporting]}</strong>.
            </Alert>
          )}

          <SubmissionRevisionBanner
            tenantId={tenantId ?? ""}
            revisedFromSubmission={submission.revisedFromSubmission}
            revisionSubmissions={submission.revisionSubmissions}
            sx={{ mx: 3 }}
          />

          <SignaturePreparationBanner submission={submission} sx={{ mx: 3 }} />

          {/* Submission Content */}
          <Box sx={{ p: 3 }}>
            <Stack spacing={3}>
              <SubmissionDisplayStatic
                submission={submission}
                showRequirementCitations={stepperEnabled}
                reviewState={reviewState}
                facilities={facilities}
                reviewers={reviewers}
                signer={signer}
                submissionDocuments={submissionDocuments}
                attachmentDocuments={attachmentDocuments}
                previewDocumentUrl={previewDocumentUrl}
                previewDocumentExtension={extensionFromName(previewDocumentName)}
                signingCompleted={signingCompleted}
                signingFlowConfirmed={signingFlowConfirmed}
                signingConfirmationPending={signingConfirmationPending}
                isPreviewedDocumentPreparedForSigning={isPreviewedDocumentPreparedForSigning}
                signatureConfirmationState={signatureConfirmationState}
                isAwaitingPostSignatureTransition={isAwaitingPostSignatureTransition}
                signingDialogOpen={signingDialogOpen}
                signingUrlData={signingUrlData}
                tenantId={tenantId ?? ""}
                isStaff={isStaff}
                onDocumentPreview={handleDocumentPreview}
                onDocumentOpenInNewTab={handleDocumentOpenInNewTab}
                onSigningDialogClose={handleSigningDialogClose}
                onSigningDialogOpen={handleSigningDialogOpen}
                onSigningCompleted={markSignatureSubmitted}
                onRetrySignatureConfirmation={retrySignatureConfirmation}
              />

              <SubmissionLinkedRecords
                tenantId={tenantId ?? ""}
                wasteStreams={(submission.wasteStreamSubmissions ?? []).map(
                  (link) => link.wasteStream
                )}
                registrations={(submission.stateWasteStreamRegistrationSubmissions ?? []).map(
                  (link) => link.registration
                )}
              />

              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <SubmissionCommentStream
                    submissionId={submission.id}
                    comments={comments}
                    commentAdded={(comment) => {
                      setComments((prev) => [...prev, comment]);
                    }}
                    commentDeleted={(commentId) => {
                      setComments((prev) => prev.filter((comment) => comment.id !== commentId));
                    }}
                  />
                </Grid>

                {showReviewOrSignPanel && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <SubmissionDisplayReviewOrSignDocument
                      isReviewerASigner={isReviewerASigner}
                      isCurrentUserStaff={isStaff}
                      hasSigner={!!signer}
                      hasCustomerReviewers={reviewers.some((r) => !r.isStaff)}
                      hasOtherPendingReviewers={reviewers.some(
                        (r) => r.user.id !== user?.id && !r.completedAt
                      )}
                      reviewState={reviewState}
                      reviewFinished={reviewFinished}
                      rejectionComment={rejectionComment}
                      submissionDocumentReviewId={submissionDocumentReviewId}
                      submissionStatus={submission.status}
                      signatureType={submission.signatureType}
                      signingCompleted={signingCompleted}
                      isMutating={isMutating}
                      control={control}
                      errors={formState.errors}
                      onSubmit={handleSubmit(handleReviewSubmit)}
                    />
                  </Grid>
                )}
              </Grid>
            </Stack>
          </Box>
        </Paper>
      </Stack>
      {previewDocumentUrl && (
        <FullScreenDocumentPreview
          open={fullScreenPreviewOpen}
          onClose={() => setFullScreenPreviewOpen(false)}
          fileUrl={previewDocumentUrl}
          fileExtension={extensionFromName(previewDocumentName)}
          title={previewDocumentName ?? "Document Preview"}
        />
      )}
    </Stack>
  );
}
